import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, Calendar, Loader2, Sparkles, Mail, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { customerAPI } from '../../services/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    marketing_consent: false
  });

  // Phone number formatting
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({ ...form, phone: formatted });
    if (errors.phone) setErrors({ ...errors, phone: null });
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    
    // First name validation
    const firstName = form.first_name.trim();
    if (!firstName) {
      newErrors.first_name = 'First name is required';
    } else if (firstName.length < 2 || firstName.length > 50) {
      newErrors.first_name = 'First name must be 2-50 characters';
    } else if (!/^[a-zA-Z\s\-]+$/.test(firstName)) {
      newErrors.first_name = 'Only letters, spaces, and hyphens allowed';
    }
    
    // Last name validation
    const lastName = form.last_name.trim();
    if (!lastName) {
      newErrors.last_name = 'Last name is required';
    } else if (lastName.length < 2 || lastName.length > 50) {
      newErrors.last_name = 'Last name must be 2-50 characters';
    } else if (!/^[a-zA-Z\s\-]+$/.test(lastName)) {
      newErrors.last_name = 'Only letters, spaces, and hyphens allowed';
    }
    
    // Phone validation
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = 'Phone number is required';
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Date of birth validation
    if (!form.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      const dob = new Date(form.date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      if (age < 18) {
        newErrors.date_of_birth = 'You must be 18 or older to join SalonO Rewards';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors above');
      return;
    }
    
    setSaving(true);
    try {
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`;
      const response = await customerAPI.update(user.id, {
        name: fullName,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone,
        date_of_birth: form.date_of_birth,
        gender: form.gender || null,
        profile_completed: true,
        preferences: {
          marketing_consent: form.marketing_consent
        }
      });
      
      updateUser(response.data);
      toast.success('Profile completed! Welcome!');
      navigate('/customer/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFF0] flex flex-col">
      {/* Header */}
      <header className="px-6 py-6 text-center">
        <h1 className="font-serif text-3xl text-[#4A154B]">Salon O</h1>
        <p className="text-[#C9A689] text-xs tracking-widest uppercase">Luxury Rewards</p>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-md animate-fadeIn">
          {/* Welcome Card */}
          <div className="glass rounded-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gradient-primary mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-serif text-2xl text-[#4A154B] mb-2">Welcome to SalonO Rewards!</h2>
              <p className="text-gray-600">Let's set up your profile to personalize your experience</p>
              
              {/* Progress Indicator */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="w-8 h-1 rounded-full bg-[#C9A689]" />
                <span className="text-xs text-gray-500">Step 1 of 1</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* First Name */}
              <div>
                <label className="form-label text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-[#C9A689]" />
                  First Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.first_name}
                  onChange={(e) => {
                    setForm({ ...form, first_name: e.target.value });
                    if (errors.first_name) setErrors({ ...errors, first_name: null });
                  }}
                  placeholder="Enter your first name"
                  className={errors.first_name ? 'border-red-500' : ''}
                  data-testid="first-name-input"
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="form-label text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-[#C9A689]" />
                  Last Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.last_name}
                  onChange={(e) => {
                    setForm({ ...form, last_name: e.target.value });
                    if (errors.last_name) setErrors({ ...errors, last_name: null });
                  }}
                  placeholder="Enter your last name"
                  className={errors.last_name ? 'border-red-500' : ''}
                  data-testid="last-name-input"
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="form-label text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#C9A689]" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  className={errors.phone ? 'border-red-500' : ''}
                  data-testid="phone-input"
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="form-label text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#C9A689]" />
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => {
                    setForm({ ...form, date_of_birth: e.target.value });
                    if (errors.date_of_birth) setErrors({ ...errors, date_of_birth: null });
                  }}
                  className={errors.date_of_birth ? 'border-red-500' : ''}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  data-testid="dob-input"
                />
                {errors.date_of_birth && (
                  <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
                )}
              </div>

              {/* Gender (Optional) */}
              <div>
                <label className="form-label text-sm text-gray-500">
                  Gender/Pronouns (Optional)
                </label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Marketing Consent */}
              <div className="flex items-start gap-3 p-4 bg-[#F5E6D3]/30 rounded-xl">
                <Checkbox
                  id="marketing_consent"
                  checked={form.marketing_consent}
                  onCheckedChange={(checked) => setForm({ ...form, marketing_consent: checked })}
                  data-testid="marketing-consent-checkbox"
                  className="mt-0.5"
                />
                <label 
                  htmlFor="marketing_consent" 
                  className="text-sm text-gray-600 cursor-pointer leading-relaxed"
                >
                  <Mail className="w-4 h-4 inline mr-1 text-[#C9A689]" />
                  I would like to receive promotional offers and updates via email
                </label>
              </div>

              {/* Privacy Notice */}
              <p className="text-xs text-gray-500 text-center">
                By continuing, you agree to our{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#C9A689] hover:text-[#4A154B] underline inline-flex items-center gap-1"
                >
                  Privacy Policy
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={saving}
                className="w-full h-12 btn-primary text-lg"
                data-testid="complete-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Complete Profile'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
